import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const sourceLabels = { category: "Category", products: "Selected Products", brand: "Brand", rule: "Rule" };

const ProductSectionTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [sections, setSections] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.productSections || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchSections = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/product-sections');
            setSections(data.sections);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load product sections", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/product-section/${id}`);
            enqueueSnackbar("Product section deleted", { variant: "success" });
            setSections((prev) => prev.filter((s) => s._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete section", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "heading",
            headerName: "Heading",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "source",
            headerName: "Source",
            minWidth: 150,
            flex: 0.5,
        },
        {
            field: "order",
            headerName: "Order",
            type: "number",
            minWidth: 80,
            flex: 0.3,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"product-section"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.heading} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = sections.map((s) => ({
        id: s._id,
        heading: s.heading,
        source: sourceLabels[s.sourceType] || s.sourceType,
        order: s.order,
        active: s.active,
    }));

    return (
        <>
            <MetaData title="Admin: Product Sections | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/site-content" className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Product Sections</h1>
                </div>
                {canCreate && (
                    <Link to="/admin/product-section/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Section</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default ProductSectionTable;